import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { HeaderComponent } from '../../../shared/salesforce/header/header.component';
import { ErrorMessageService } from '../../shared/services/error-message.service';
import { RedirectHelperService } from '../../shared/services/redirect-helper.service';
import { SalesForceAuthService } from '../../shared/services/sales-force-auth.service';

@Component({
  selector: 'app-sales-force-verify',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, HeaderComponent],
  templateUrl: './verify.component.html',
  styleUrls: ['./verify.component.css'],
})
export class SalesForceVerifyComponent implements OnInit, OnDestroy {
  otpForm: FormGroup;
  timer: number = 90;
  timerInterval: any;
  debounceTimeout: any = null;
  debouncing: boolean = false;
  loading = false;
  errorMessage: string | null = null;
  selectedMethod: 'email' | 'wab' = 'email';
  hasTriedAnyMethod: boolean = false;

  private sessionId: string = '';
  private trxId: string = '';
  private username: string = '';
  private errorCode: string | null = null;
  private errorDefaultMessage: string | null = null;
  private langChangeSubscription?: Subscription;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  constructor(
    private translate: TranslateService,
    private authService: SalesForceAuthService,
    private router: Router,
    private fb: FormBuilder,
    private redirectHelper: RedirectHelperService,
    private errorMessageService: ErrorMessageService
  ) {
    this.otpForm = this.fb.group({
      digits: this.fb.array(Array(6).fill('').map(() => new FormControl('', [Validators.pattern('[0-9]')]))),
    });
  }

  ngOnInit(): void {
    this.sessionId = sessionStorage.getItem('salesforce_sessionId') || '';
    this.trxId = sessionStorage.getItem('salesforce_trxId') || '';
    this.username = sessionStorage.getItem('salesforce_username') || '';

    if (!this.sessionId || !this.trxId) {
      this.router.navigate(['/customidp/salesforce/login']);
      return;
    }

    const initialMethod = sessionStorage.getItem('salesforce_initialMethod') || 'email';
    this.selectedMethod = initialMethod as 'email' | 'wab';
    
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.updateErrorMessage();
    });
    
    this.startTimer();
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  get otpControls() {
    return (this.otpForm.get('digits') as FormArray).controls;
  }

  private updateErrorMessage(): void {
    if (this.errorCode && this.errorDefaultMessage) {
      this.errorMessage = this.errorMessageService.getErrorMessage(
        this.errorCode,
        this.errorDefaultMessage
      );
    }
  }

  private setError(errorCode: string, defaultMessage: string): void {
    this.errorCode = errorCode;
    this.errorDefaultMessage = defaultMessage;
    this.updateErrorMessage();
  }

  private clearError(): void {
    this.errorCode = null;
    this.errorDefaultMessage = null;
    this.errorMessage = null;
  }

  startTimer(): void {
    this.timer = 90;
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
      } else {
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  isFormValid(): boolean {
    const digits = this.otpForm.get('digits') as FormArray;
    return digits.valid && digits.value.every((digit: string) => digit !== '');
  }

  onOtpInput(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    let value = target.value;

    if (isNaN(+value)) {
      this.otpForm.get('digits')?.get('' + index)?.setValue('');
      return;
    }
    value = value.replace(/\D/g, '');

    if (value && index < 5) {
      this.otpInputs.get(index + 1)?.nativeElement.focus();
    }

    if (this.isFormValid()) {
      setTimeout(() => this.verifyOTP(), 100);
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && index > 0 && !this.otpForm.get('digits')?.get('' + index)?.value) {
      this.otpInputs.get(index - 1)?.nativeElement.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text').trim().slice(0, 6);
    if (pastedData && /^\d{6}$/.test(pastedData)) {
      const digits = this.otpForm.get('digits') as FormArray;
      pastedData.split('').forEach((char, index) => {
        digits.at(index).setValue(char);
      });
      this.otpInputs.last.nativeElement.focus();
      setTimeout(() => this.verifyOTP(), 100);
    }
  }

  verifyOTP(): void {
    this.clearError();

    if (!this.isFormValid()) {
      this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.COMPLETE_6_DIGIT_CODE'));
      return;
    }

    if (!this.sessionId || !this.trxId) {
      this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.SESSION_EXPIRED'));
      return;
    }

    this.loading = true;
    const otp = this.otpForm.value.digits.join('');
    
    this.authService.submitOTP(this.sessionId, otp, this.trxId).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.errorCode === '0' && response.data?.authCode) {
          const authCode = response.data.authCode;

          const storedRole = sessionStorage.getItem('salesforce_selectedRole');
          const selectedRole = response.data?.selectedRole || storedRole || 'N_GS';

          this.redirectHelper.redirectByRole(authCode, selectedRole);

          setTimeout(() => {
            sessionStorage.removeItem('salesforce_sessionId');
            sessionStorage.removeItem('salesforce_trxId');
            sessionStorage.removeItem('salesforce_username');
            sessionStorage.removeItem('salesforce_selectedRole');
          }, 100);
        } else {
          this.setError(
            response.errorCode || '9999',
            this.translate.instant('SALESFORCE.VERIFY.OTP_VERIFICATION_FAILED')
          );
        }
      },
      error: (error) => {
        this.loading = false;
        this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.NETWORK_ERROR'));
        console.error('Sales Force OTP verification error:', error);
      },
    });
  }

  resendCode(): void {
    if (this.debouncing) return;
    
    this.debouncing = true;
    this.clearError();
    
    this.debounceTimeout = setTimeout(() => {
      this.debouncing = false;
    }, 2000);

    this.authService.resendOTP(this.sessionId, this.selectedMethod).subscribe({
      next: (response) => {
        if (response.errorCode === '0' || response.success) {
          this.resetOtpForm();
          this.startTimer();
        } else {
          this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.RESEND_OTP_FAILED'));
        }
      },
      error: (error) => {
        this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.RESEND_OTP_FAILED'));
        console.error('Resend OTP error:', error);
      },
    });
  }

  switchToWab(): void {
    if (this.debouncing) return;
    
    this.debouncing = true;
    this.clearError();
    
    this.debounceTimeout = setTimeout(() => {
      this.debouncing = false;
    }, 2000);

    this.authService.resendOTP(this.sessionId, 'wab').subscribe({
      next: (response) => {
        if (response.errorCode === '0' || response.success) {
          this.selectedMethod = 'wab';
          this.hasTriedAnyMethod = true;
          this.resetOtpForm();
          this.startTimer();
        } else {
          this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.RESEND_OTP_FAILED'));
        }
      },
      error: (error) => {
        this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.RESEND_OTP_FAILED'));
        console.error('Switch to WhatsApp error:', error);
      }
    });
  }

  switchToEmail(): void {
    if (this.debouncing) return;
    
    this.debouncing = true;
    this.clearError();
    
    this.debounceTimeout = setTimeout(() => {
      this.debouncing = false;
    }, 2000);

    this.authService.resendOTP(this.sessionId, 'email').subscribe({
      next: (response) => {
        if (response.errorCode === '0' || response.success) {
          this.selectedMethod = 'email';
          this.hasTriedAnyMethod = true;
          this.resetOtpForm();
          this.startTimer();
        } else {
          this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.RESEND_OTP_FAILED'));
        }
      },
      error: (error) => {
        this.setError('9999', this.translate.instant('SALESFORCE.VERIFY.RESEND_OTP_FAILED'));
        console.error('Switch to email error:', error);
      }
    });
  }

  resetOtpForm(): void {
    const digits = this.otpForm.get('digits') as FormArray;
    digits.controls.forEach(control => control.setValue(''));
    if (this.otpInputs?.first) {
      this.otpInputs.first.nativeElement.focus();
    }
  }

}